import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, X } from 'lucide-react';

const Handoffs = () => {
  const { isAdminOrTL, isReadOnly } = useAuth();
  const [handoffs, setHandoffs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState({
    handoff_date: new Date().toISOString().split('T')[0],
    prospect_name: '', prospect_company: '', prospect_vertical: '',
    handoff_type: 'a', prospect_reply_verbatim: '',
    call_booked: 'pending', call_date: '', call_outcome: '',
    estimated_deal_value: '', notes: ''
  });

  const fetchHandoffs = async () => {
    try {
      const params = filter ? `?type=${filter}` : '';
      const res = await api.get(`/handoffs${params}`);
      setHandoffs(res.data.handoffs || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHandoffs(); }, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/handoffs', form);
      setShowForm(false);
      setForm({
        handoff_date: new Date().toISOString().split('T')[0],
        prospect_name: '', prospect_company: '', prospect_vertical: '',
        handoff_type: 'a', prospect_reply_verbatim: '',
        call_booked: 'pending', call_date: '', call_outcome: '',
        estimated_deal_value: '', notes: ''
      });
      fetchHandoffs();
    } catch (err) { alert(err.response?.data?.error || 'Error saving handoff.'); }
  };

  const getTypeBadge = (type) => {
    const map = {
      a: ['Type A', 'badge-green'],
      b_reveal: ['B-Reveal', 'badge-green'],
      b_confirm: ['B-Confirm', 'badge-amber'],
      b_no_reply: ['B-No Reply', 'badge-gray']
    };
    const [label, cls] = map[type] || ['Unknown', 'badge-gray'];
    return <span className={`badge ${cls}`}>{label}</span>;
  };

  const getOutcomeBadge = (outcome) => {
    const map = {
      proposal: 'badge-green', hold: 'badge-amber', drop: 'badge-red',
      no_show: 'badge-gray', not_yet: 'badge-blue'
    };
    return outcome ? <span className={`badge ${map[outcome] || 'badge-gray'}`}>{outcome}</span> : '-';
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h2>Handoff Tracking</h2>
          <p>Log and track Type A/B prospect handoffs</p>
        </div>
        {!isReadOnly && (
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> New Handoff</>}
          </button>
        )}
      </div>

      {!isReadOnly && showForm && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Log New Handoff</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.handoff_date} onChange={e => setForm({...form, handoff_date: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Prospect Name</label>
                <input value={form.prospect_name} onChange={e => setForm({...form, prospect_name: e.target.value})} placeholder="John Smith" />
              </div>
              <div className="form-group">
                <label>Company</label>
                <input value={form.prospect_company} onChange={e => setForm({...form, prospect_company: e.target.value})} placeholder="Acme Corp" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Vertical</label>
                <select value={form.prospect_vertical} onChange={e => setForm({...form, prospect_vertical: e.target.value})}>
                  <option value="">Select...</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="fintech">FinTech</option>
                  <option value="real_estate_proptech">Real Estate / PropTech</option>
                  <option value="edtech">EdTech</option>
                  <option value="consulting_enterprise">Consulting / Enterprise</option>
                  <option value="saas_general">SaaS / General</option>
                  <option value="b2c">B2C</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Handoff Type</label>
                <select value={form.handoff_type} onChange={e => setForm({...form, handoff_type: e.target.value})} required>
                  <option value="a">Type A — Project Named</option>
                  <option value="b_reveal">B-Reveal — Revealed After 2nd Msg</option>
                  <option value="b_confirm">B-Confirm — Exploratory Close</option>
                  <option value="b_no_reply">B-No Reply — Silent Close</option>
                </select>
              </div>
              <div className="form-group">
                <label>Call Booked</label>
                <select value={form.call_booked} onChange={e => setForm({...form, call_booked: e.target.value})}>
                  <option value="pending">Pending</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {(form.handoff_type === 'b_reveal' || form.handoff_type === 'b_confirm' || form.handoff_type === 'b_no_reply') && (
              <div className="form-group">
                <label>Prospect's Reply to 2nd Message (paste verbatim)</label>
                <textarea value={form.prospect_reply_verbatim} onChange={e => setForm({...form, prospect_reply_verbatim: e.target.value})}
                  placeholder="Paste exactly what the prospect said..." />
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>Call Date</label>
                <input type="date" value={form.call_date} onChange={e => setForm({...form, call_date: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Call Outcome</label>
                <select value={form.call_outcome} onChange={e => setForm({...form, call_outcome: e.target.value})}>
                  <option value="">Not yet</option>
                  <option value="proposal">Proposal</option>
                  <option value="hold">Hold</option>
                  <option value="drop">Drop</option>
                  <option value="no_show">No Show</option>
                </select>
              </div>
              <div className="form-group">
                <label>Estimated Deal Value</label>
                <input value={form.estimated_deal_value} onChange={e => setForm({...form, estimated_deal_value: e.target.value})} placeholder="$5,000" />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Any context..." />
            </div>
            <button type="submit" className="btn btn-primary">Save Handoff</button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>All Handoffs</h3>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
            <option value="">All Types</option>
            <option value="a">Type A</option>
            <option value="b_reveal">B-Reveal</option>
            <option value="b_confirm">B-Confirm</option>
            <option value="b_no_reply">B-No Reply</option>
          </select>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                {isAdminOrTL && <th>BDE</th>}
                <th>Prospect</th><th>Company</th><th>Type</th>
                <th>Call</th><th>Outcome</th><th>Value</th>
              </tr>
            </thead>
            <tbody>
              {handoffs.map(h => (
                <tr key={h.id}>
                  <td>{new Date(h.handoff_date).toLocaleDateString()}</td>
                  {isAdminOrTL && <td>{h.bde_name}</td>}
                  <td><strong>{h.prospect_name || '-'}</strong></td>
                  <td>{h.prospect_company || '-'}</td>
                  <td>{getTypeBadge(h.handoff_type)}</td>
                  <td>{h.call_booked === 'yes' ? <span className="badge badge-green">Booked</span> : h.call_booked === 'pending' ? <span className="badge badge-amber">Pending</span> : '-'}</td>
                  <td>{getOutcomeBadge(h.call_outcome)}</td>
                  <td>{h.estimated_deal_value || '-'}</td>
                </tr>
              ))}
              {handoffs.length === 0 && (
                <tr><td colSpan={isAdminOrTL ? 8 : 7} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No handoffs logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Handoffs;
